
import React, { useState } from 'react';
import { Copy, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ReportData } from '@/types/report';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Badge } from '@/components/ui/badge';

interface ReportContentSectionProps {
  reportText: string;
  report?: ReportData;
}

export const ReportContentSection: React.FC<ReportContentSectionProps> = ({ reportText, report }) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      toast({
        title: 'คัดลอกรายงานแล้ว',
        description: 'รายงานได้ถูกคัดลอกไปยังคลิปบอร์ดแล้ว',
      });
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast({
        title: 'ไม่สามารถคัดลอกได้',
        description: 'โปรดลองอีกครั้ง',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold">รายงานฉบับเต็ม</h3>
        <Button 
          variant="outline" 
          size="sm"
          className="flex items-center gap-2"
          onClick={handleCopy}
        >
          <Copy size={16} />
          {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
        </Button>
      </div>
      
      <Textarea
        value={reportText}
        readOnly
        className="min-h-[400px] bg-gray-50"
      />

      {/* Check-in Photos Section */}
      {report?.check_in_photo_urls && report.check_in_photo_urls.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-bold mb-3">รูปถ่ายเช็คอิน</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {report.check_in_photo_urls.map((photoUrl, index) => (
              <div 
                key={index} 
                className="cursor-pointer border rounded-md overflow-hidden hover:opacity-80 transition-opacity"
                onClick={() => setSelectedImage(photoUrl)}
              >
                <AspectRatio ratio={4/3} className="bg-gray-100">
                  <img 
                    src={photoUrl} 
                    alt={`Check-in photo ${index + 1}`}
                    className="object-cover w-full h-full"
                  />
                </AspectRatio>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Worksheet URLs Section */}
      {report?.worksheet_urls && report.worksheet_urls.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-bold mb-3">เอกสารเวิร์กชีท</h3>
          <div className="flex flex-wrap gap-2">
            {report.worksheet_urls.map((url, index) => (
              <Badge key={index} variant="outline" className="px-3 py-1">
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:underline"
                >
                  เอกสาร {index + 1} <ExternalLink size={14} />
                </a>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Worksheet Drive Link */}
      {report?.worksheet_drive_link && (
        <div className="mt-4">
          <h3 className="text-lg font-bold mb-3">ลิงก์ Google Drive</h3>
          <Badge variant="outline" className="px-3 py-1">
            <a 
              href={report.worksheet_drive_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:underline"
            >
              เปิดลิงก์ Google Drive <ExternalLink size={14} />
            </a>
          </Badge>
        </div>
      )}

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
        <DialogContent className="max-w-3xl w-[90vw]">
          <DialogHeader>
            <DialogTitle>รูปถ่ายเช็คอิน</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="relative w-full">
              <img 
                src={selectedImage} 
                alt="Check-in photo" 
                className="w-full object-contain max-h-[70vh]"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
